import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { B2BClientService } from '../../../services/B2BServices';
import { getLocations } from '../../../services/LocationService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { DISCOUNT_TYPE } from '../../../utils/Enums';
import { formatMoney } from '../../../utils/currency';

const emptyNewItem = {
  productId: '',
  productName: '',
  quantity: '',
  unitPrice: '',
  discountType: DISCOUNT_TYPE.PERCENTAGE,
  discountValue: 0
};

const EditProformaQuantitiesModal = ({ open, onClose, proforma, onSave }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [branchId, setBranchId] = useState('');
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [newItem, setNewItem] = useState(emptyNewItem);
  const [saving, setSaving] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    if (!open || !proforma) return;

    setBranchId(proforma.branchId || '');
    setItems((proforma.items || []).map(item => ({
      ...item,
      quantity: item.quantity ?? 0,
      requestedQuantity: item.requestedQuantity ?? item.quantity ?? 0,
      discountType: item.discountType || DISCOUNT_TYPE.PERCENTAGE,
      discountValue: item.discountValue ?? 0,
      rowKey: `existing-${item.id}`
    })));
    setNotes(proforma.notes || '');
    setNewItem(emptyNewItem);
  }, [open, proforma]);

  useEffect(() => {
    if (!open) return;

    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);
        const locationsData = await getLocations();
        setLocations((locationsData || []).filter(location => !location.isPlant));
      } catch (error) {
        console.error('Error fetching locations:', error);
        setLocations([]);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  }, [open]);

  useEffect(() => {
    if (!open || !proforma?.clientId || !branchId) {
      setProducts([]);
      return;
    }

    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const response = await B2BClientService.getOrderProducts(proforma.clientId, branchId);
        setProducts(response.products || []);
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [open, proforma?.clientId, branchId]);

  useEffect(() => {
    if (!open || !proforma || !products.length || Number(branchId) === Number(proforma.branchId)) return;

    setItems(prev => prev.map(item => {
      const branchProduct = products.find(product => product.id === item.productId);
      if (!branchProduct) return item;

      return {
        ...item,
        unitPrice: branchProduct.unitPrice ?? branchProduct.price ?? item.unitPrice,
        discountType: branchProduct.discountType || item.discountType || DISCOUNT_TYPE.PERCENTAGE,
        discountValue: branchProduct.discountValue ?? item.discountValue ?? 0
      };
    }));
  }, [open, proforma, products, branchId]);

  const formatCurrency = (amount) => (
    formatMoney(Number(amount) || 0)
  );

  const calculateFinalUnitPrice = (item) => {
    const unitPrice = Number(item.unitPrice) || 0;
    const discountValue = Number(item.discountValue) || 0;
    if (item.discountType === DISCOUNT_TYPE.PERCENTAGE || item.discountType === 'PERCENTAGE') {
      return unitPrice * (1 - discountValue / 100);
    }
    if (item.discountType === DISCOUNT_TYPE.AMOUNT || item.discountType === 'AMOUNT') {
      return Math.max(0, unitPrice - discountValue);
    }
    return unitPrice;
  };

  const totalAmount = useMemo(() => (
    items.reduce((total, item) => total + calculateFinalUnitPrice(item) * (Number(item.quantity) || 0), 0)
  ), [items]);

  const handleQuantityChange = (rowKey, value) => {
    const parsed = value === '' ? '' : Math.max(0, Number(value));
    setItems(prev => prev.map(item => (
      item.rowKey === rowKey ? { ...item, quantity: parsed } : item
    )));
  };

  const handleRemoveItem = (rowKey) => {
    setItems(prev => prev.filter(item => item.rowKey !== rowKey));
  };

  const handleNewItemFieldChange = (field, value) => {
    setNewItem(prev => ({ ...prev, [field]: value }));
  };

  const handleAddItem = () => {
    if (!newItem.productId) {
      SnackbarUtils.error(t('product_required'));
      return;
    }
    if (!newItem.quantity || Number(newItem.quantity) <= 0) {
      SnackbarUtils.error(t('quantity_required'));
      return;
    }

    const selectedProduct = products.find(product => product.id === newItem.productId);
    if (!selectedProduct) {
      SnackbarUtils.error(t('product_required'));
      return;
    }

    const alreadyExists = items.some(item => item.productId === selectedProduct.id);
    if (alreadyExists) {
      SnackbarUtils.error('Ese producto ya está en la proforma. Modificá la cantidad en la fila existente.');
      return;
    }

    setItems(prev => ([
      ...prev,
      {
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        quantity: Number(newItem.quantity),
        requestedQuantity: Number(newItem.quantity),
        unitPrice: Number(newItem.unitPrice) || 0,
        discountType: newItem.discountType || DISCOUNT_TYPE.PERCENTAGE,
        discountValue: Number(newItem.discountValue) || 0,
        rowKey: `new-${selectedProduct.id}-${Date.now()}`
      }
    ]));
    setNewItem(emptyNewItem);
  };

  const handleClose = () => {
    setSaving(false);
    onClose();
  };

  const handleSave = async () => {
    if (!branchId) {
      SnackbarUtils.error(t('branch_required'));
      return;
    }

    if (!items.some(item => Number(item.quantity) > 0)) {
      SnackbarUtils.error('Debe quedar al menos un item con cantidad aceptada mayor a 0');
      return;
    }

    const normalizedItems = items.map(item => ({
      id: item.id || null,
      productId: item.productId,
      quantity: Number(item.quantity) || 0,
      requestedQuantity: Number(item.requestedQuantity ?? item.quantity) || 0,
      unitPrice: Number(item.unitPrice) || 0,
      discountType: item.discountType || DISCOUNT_TYPE.PERCENTAGE,
      discountValue: Number(item.discountValue) || 0
    }));

    await saveProforma({
      branchId: Number(branchId),
      notes: notes.trim() || null,
      items: normalizedItems
    });
  };

  const saveProforma = async (payload) => {
    try {
      setSaving(true);
      await onSave(proforma.id, payload);
      handleClose();
    } catch (error) {
      console.error('Error updating proforma:', error);
      SnackbarUtils.error(t('error_updating_status'));
    } finally {
      setSaving(false);
    }
  };

  if (!proforma) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>Modificar proforma</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Ajustá la sucursal, eliminá productos, agregá nuevos y modificá las cantidades aceptadas.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>{t('branch')}</InputLabel>
                <Select
                  value={branchId}
                  label={t('branch')}
                  onChange={(event) => {
                    setBranchId(Number(event.target.value));
                    setNewItem(emptyNewItem);
                  }}
                  disabled={loadingLocations}
                >
                  {loadingLocations ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} />
                      {t('loading')}...
                    </MenuItem>
                  ) : (
                    locations.map(location => (
                      <MenuItem key={location.id} value={location.id}>
                        {location.name || `Sucursal ${location.id}`}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t('client')}
                value={proforma.clientName || `Cliente ${proforma.clientId}`}
                fullWidth
                disabled
              />
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
              Agregar producto
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <Autocomplete
                  fullWidth
                  options={products}
                  getOptionLabel={(option) => option.name || ''}
                  value={products.find(product => product.id === newItem.productId) || null}
                  onChange={(event, selectedProduct) => {
                    if (!selectedProduct) {
                      setNewItem(emptyNewItem);
                      return;
                    }

                    setNewItem({
                      productId: selectedProduct.id,
                      productName: selectedProduct.name,
                      quantity: '',
                      unitPrice: selectedProduct.unitPrice ?? selectedProduct.price ?? 0,
                      discountType: selectedProduct.discountType || DISCOUNT_TYPE.PERCENTAGE,
                      discountValue: selectedProduct.discountValue ?? 0
                    });
                  }}
                  disabled={loadingProducts || !branchId}
                  loading={loadingProducts}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('product')}
                      placeholder={!branchId ? t('select_branch_first') : t('search_product')}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label={t('quantity')}
                  fullWidth
                  type="number"
                  value={newItem.quantity}
                  onChange={(event) => handleNewItemFieldChange('quantity', Math.max(0, Number(event.target.value)))}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label={t('unit_price')}
                  fullWidth
                  value={formatCurrency(newItem.unitPrice)}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label={t('discount')}
                  fullWidth
                  value={
                    newItem.discountType === DISCOUNT_TYPE.AMOUNT || newItem.discountType === 'AMOUNT'
                      ? formatCurrency(newItem.discountValue)
                      : `${Number(newItem.discountValue) || 0}%`
                  }
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={1}>
                <Button
                  variant="contained"
                  onClick={handleAddItem}
                  disabled={!newItem.productId || !newItem.quantity}
                  sx={{ minWidth: 48, height: 48 }}
                >
                  <AddIcon />
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('product')}</TableCell>
                  <TableCell align="center">Solicitado</TableCell>
                  <TableCell align="center">Aceptado</TableCell>
                  <TableCell align="center">Stock actual</TableCell>
                  <TableCell align="right">Precio final</TableCell>
                  <TableCell align="right">Subtotal</TableCell>
                  <TableCell align="center">{t('actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(item => {
                  const finalUnitPrice = calculateFinalUnitPrice(item);
                  return (
                    <TableRow key={item.rowKey}>
                      <TableCell>{item.productName}</TableCell>
                      <TableCell align="center">{item.requestedQuantity}</TableCell>
                      <TableCell align="center" sx={{ width: 140 }}>
                        <TextField
                          size="small"
                          type="number"
                          value={item.quantity}
                          onChange={(event) => handleQuantityChange(item.rowKey, event.target.value)}
                          inputProps={{ min: 0 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {/* El stock es el de la sucursal original de la proforma; si cambian la sucursal ya no corresponde. */}
                        {item.currentStock != null && Number(branchId) === Number(proforma.branchId) ? (
                          <Typography
                            variant="body2"
                            color={item.currentStock < (Number(item.quantity) || 0) ? 'error.main' : 'text.primary'}
                            sx={{ fontWeight: item.currentStock < (Number(item.quantity) || 0) ? 700 : 400 }}
                          >
                            {item.currentStock}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(finalUnitPrice)}</TableCell>
                      <TableCell align="right">{formatCurrency(finalUnitPrice * (Number(item.quantity) || 0))}</TableCell>
                      <TableCell align="center">
                        <IconButton color="error" onClick={() => handleRemoveItem(item.rowKey)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Total aceptado: {formatCurrency(totalAmount)}
              </Typography>
            </Box>
          </TableContainer>

          <TextField
            label={t('notes')}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>{t('cancel')}</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : t('save')}
        </Button>
      </DialogActions>

    </Dialog>
  );
};

export default EditProformaQuantitiesModal;
